import { AbortControllerManager } from '../abortControllerManager';
import { authClient, kioskClient, publicClient } from '../client';
import { ITemplateResponse } from '@/types/common';
import { TVisitNotesTemplate } from '@/types/formBuilder';
import {
  IAllInstitutionsResponse,
  IInstitutionNotSignedServer,
  IInstitutionPatientRequest,
  IInstitutionResponse,
  IInstitutionServer,
  IServiceServer,
} from '@/types/institutions';
import { IUserData } from '@/types/user';

interface IImageLinkResponse {
  data: {
    data: string;
  };
}

type TServiceServerWithoutId = Omit<IServiceServer, 'id'>;

type TInstitutionServerForUpdate = Omit<
  IInstitutionServer,
  'services' | 'updated_at'
> & {
  services: TServiceServerWithoutId[];
};

class InstitutionsApi extends AbortControllerManager {
  constructor() {
    super();
  }

  getInstitutionsNotSigned(): Promise<
    ITemplateResponse<IInstitutionNotSignedServer[]>
  > {
    const url = '/v2/institutions';
    const signal = this.createAbortController('getInstitutions');
    return publicClient.get(url, { signal });
  }

  getInstitutions(): Promise<IAllInstitutionsResponse> {
    const url = '/v2/institutions';
    const signal = this.createAbortController('getInstitutions');
    return authClient.get(url, { signal });
  }

  getKioskInstitutions(): Promise<IAllInstitutionsResponse> {
    const url = '/v2/institutions';
    const signal = this.createAbortController('getKioskInstitutions');
    return kioskClient.get(url, { signal });
  }

  createInstitution(
    payload: Partial<IInstitutionServer>,
  ): Promise<IInstitutionResponse> {
    const url = '/v2/institutions';
    const signal = this.createAbortController('createInstitution');
    return authClient.post(url, payload, { signal });
  }

  updateInstitution(
    id: string,
    payload: Partial<TInstitutionServerForUpdate>,
  ): Promise<IInstitutionResponse> {
    const url = `/v2/institutions/${id}`;
    const signal = this.createAbortController(`updateInstitution-${id}`);
    return authClient.put(url, payload, { signal });
  }

  patchInstitution(
    id: string,
    payload: Partial<IInstitutionServer>,
  ): Promise<IInstitutionResponse> {
    const url = `/v2/institutions/${id}`;
    const signal = this.createAbortController(`patchInstitution-${id}`);
    return authClient.patch(url, payload, { signal });
  }

  patchKioskInstitution(
    id: string,
    payload: Partial<IInstitutionServer>,
  ): Promise<IInstitutionResponse> {
    const url = `/v2/institutions/${id}`;
    const signal = this.createAbortController(`patchKioskInstitution-${id}`);
    return kioskClient.patch(url, payload, { signal });
  }

  uploadInstitutionImage(data: FormData): Promise<IImageLinkResponse> {
    const url = '/v2/institutions/image';
    const signal = this.createAbortController('uploadInstitutionImage');
    return authClient.post(url, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
      signal,
    });
  }

  // VISIT NOTES TEMPLATES METHODS
  getVisitNotesTemplates(
    institutionId: string,
  ): Promise<ITemplateResponse<TVisitNotesTemplate[]>> {
    const signal = this.createAbortController(
      `getVisitNotesTemplates-${institutionId}`,
    );
    return authClient.get(`/v2/institutions/${institutionId}/visit-notes`, {
      signal,
    });
  }

  createVisitNotesTemplate(
    institutionId: string,
    body: Pick<TVisitNotesTemplate, 'data' | 'is_active'>,
  ): Promise<ITemplateResponse<TVisitNotesTemplate>> {
    const signal = this.createAbortController('createVisitNotesTemplate');
    return authClient.post(
      `/v2/institutions/${institutionId}/visit-notes`,
      body,
      {
        signal,
      },
    );
  }

  updateVisitNotesTemplate(
    id: string,
    body: Pick<TVisitNotesTemplate, 'data' | 'is_active'>,
  ): Promise<ITemplateResponse<TVisitNotesTemplate>> {
    const signal = this.createAbortController(`updateVisitNotesTemplate-${id}`);
    return authClient.put(`/v2/institutions/visit-notes/${id}`, body, {
      signal,
    });
  }

  deleteVisitNotesTemplate(id: string): Promise<void> {
    const signal = this.createAbortController(`deleteVisitNotesTemplate-${id}`);
    return authClient.delete(`/v2/institutions/visit-notes/${id}`, {
      signal,
    });
  }

  connectDoseSpot(id: string): Promise<IInstitutionResponse> {
    const signal = this.createAbortController(`connectDoseSpot-${id}`);
    return authClient.post(
      `/v2/institutions/${id}/dosespot/connect`,
      {},
      {
        signal,
      },
    );
  }

  disconnectDoseSpot(id: string): Promise<IInstitutionResponse> {
    const signal = this.createAbortController(`disconnectDoseSpot-${id}`);
    return authClient.post(
      `/v2/institutions/${id}/dosespot/disconnect`,
      {},
      {
        signal,
      },
    );
  }

  createInstitutionPatient(
    institutionId: string,
    body: IInstitutionPatientRequest,
  ): Promise<ITemplateResponse<IUserData>> {
    const signal = this.createAbortController(
      `createInstitutionPatient-${institutionId}`,
    );
    return authClient.post(`/v2/institutions/${institutionId}/patients`, body, {
      signal,
    });
  }
}

export const institutionsApi = new InstitutionsApi();
